// Apolo IDE — Hook runner.
//
// Hook Powerstack Sprint v1.
//
// `fireHooks(event, context)` looks up enabled hooks for the event in the
// cached registry (fetched via /api/code-ide/hooks), filters by matcher
// regex against `context.tool`, executes them through the Electron IPC
// (`window.apolo.hooks.run`), and reports back results.
//
// Blocking semantics: if a hook with `blocking=true` exits non-zero, the
// flow MUST halt. Callers check `out.blocked` and abort the action.
//
// This module is renderer-side; the actual script execution happens in
// the Electron main process via `electron/hooks.cjs`.

import { apiFetch } from './api.js';

let _cache = null;
let _fetcher = defaultFetcher;

async function defaultFetcher() {
  const res = await apiFetch('/api/code-ide/hooks');
  if (!res.ok) return [];
  return res.data?.hooks || [];
}

/**
 * Test seam — swap the hook fetcher.
 * @internal
 */
export function _setHooksFetcher(fn) {
  _fetcher = typeof fn === 'function' ? fn : defaultFetcher;
}

/**
 * Test seam — clear cached registry between tests.
 * @internal
 */
export function _resetHooksCache() {
  _cache = null;
  _fetcher = defaultFetcher;
}

/**
 * Force a re-fetch of the registry. Call after the user adds/edits/removes
 * a hook so the next fire picks it up.
 */
export function invalidateHooksCache() {
  _cache = null;
}

async function getHooks() {
  if (_cache) return _cache;
  try {
    _cache = await _fetcher();
  } catch {
    _cache = [];
  }
  return _cache;
}

function matchesMatcher(matcher, tool) {
  if (!matcher) return true;
  try {
    return new RegExp(matcher).test(String(tool || ''));
  } catch {
    // Invalid regex — graceful fallback to "match all" so user-typed
    // bad patterns never break the flow.
    return true;
  }
}

/**
 * Fire all matching hooks for an event.
 *
 * @param {string} event           - 'pre_tool_call' | 'post_tool_call' | 'stop' | 'user_prompt_submit'
 * @param {object} context         - injected as APOLO_* env vars in shell hooks
 * @returns {Promise<{
 *   ran: Array<{hookId:string, exitCode:number, ok:boolean, stdout:string, stderr:string}>,
 *   blocked: boolean,
 *   blockingHook?: string,
 *   blockingStderr?: string,
 *   reason?: string,
 * }>}
 */
export async function fireHooks(event, context = {}) {
  // Electron bridge required for actual shell execution. `window.apolo` is the preload
  // bridge (untyped on Window) — capture it as one typed-any local so this stays clean
  // under checkJs (the same access pattern SessionWorkspace uses ad-hoc).
  const bridge = typeof window !== 'undefined' ? /** @type {any} */ (window).apolo : null;
  if (!bridge?.hooks?.run) {
    return { ran: [], blocked: false, reason: 'no_electron_bridge' };
  }

  const all = await getHooks();
  const matching = all.filter((h) =>
    h.enabled !== false
    && h.event === event
    && matchesMatcher(h.matcher, context.tool),
  );

  const ran = [];
  for (const hook of matching) {
    let result;
    try {
      result = await bridge.hooks.run({
        script: hook.script,
        runtime: hook.runtime || 'shell',
        context,
        timeoutMs: hook.timeout_ms || 30000,
      });
    } catch (e) {
      result = { ok: false, exitCode: -1, stdout: '', stderr: e?.message || 'ipc failed' };
    }
    const exitCode = typeof result?.exitCode === 'number' ? result.exitCode : (result?.ok ? 0 : 1);
    ran.push({
      hookId: hook.id,
      exitCode,
      ok: result?.ok ?? false,
      stdout: result?.stdout || '',
      stderr: result?.stderr || '',
    });
    if (hook.blocking && exitCode !== 0) {
      return {
        ran,
        blocked: true,
        blockingHook: hook.id,
        blockingStderr: result?.stderr || '',
      };
    }
  }
  return { ran, blocked: false };
}
